import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusSquare } from '@fortawesome/pro-solid-svg-icons';
import { faTrashAlt, faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import EnergyTracerTable from '../tables';
import { ROLES, ROLE_NAMES_UPPER, ROLE_WEIGHTS } from '../../helpers/RolesData';
import AppContext from '../../contexts/app-context';


const styles = {
  column: {
    padding: '22px 15px 0px 15px',
  },
  actionColumn: {
    padding: '16px 15px 0px 15px',
  },
};

const STATIC_COLUMNS = [
  {
    dataField: 'email',
    text: 'Email',
    style: styles.column,
  },
  {
    dataField: 'firstName',
    text: 'First Name',
    style: styles.column,
  },
  {
    dataField: 'lastName',
    text: 'Last Name',
    style: styles.column,
  },
];

export default function UserTable(props) {
  const {
    entityType,
    users,
    deleteUser,
    saveUserRoles,
    setUsers,
    addUserToken,
    deleteUserToken,
    updateAccess,
    deleteAccess,
    roleWeight,
  } = props;

  const { userId } = useContext(AppContext);

  // Allows immediate UI feedback onChange. Value is corrected if an error occurs.
  const changeUserRole = (rowId, userRole, oldRole) => {
    setUsers((prev) =>
      prev.map((user) => {
        if (user.id !== rowId) return user;
        else {
          user.role = userRole;
          return user;
        }
      })
    );
    saveUserRoles(rowId, userRole, oldRole);
  };

  const userColumns = [
    ...STATIC_COLUMNS,
    {
      dataField: 'role',
      text: 'Role',
      style: styles.actionColumn,
      formatter: (cell, row) => (
        <UserRoleSelect
          rowId={row.id}
          userId={userId}
          userRole={cell}
          saveUserRoles={saveUserRoles}
          changeUserRole={changeUserRole}
          disabled={!updateAccess}
          roleWeight={roleWeight}
        />
      ),
    },
  ];

  const actionColumns = [
    {
      dataField: 'id',
      text: 'Actions',
      style: styles.actionColumn,
      formatter: (cell, row) =>
        roleWeight > ROLE_WEIGHTS[row.role] || roleWeight === 3 || row.id === userId ? (
          <UserActions rowId={cell} deleteUser={deleteUser} />
        ) : (
          <></>
        ),
    },
  ];

  const tokenColumns = [
    {
      dataField: 'token',
      text: 'API Token',
      style: styles.actionColumn,
      formatter: (cell, row) => (
        <UserToken
          token={cell}
          rowId={row['id']}
          addUserToken={addUserToken}
          deleteUserToken={deleteUserToken}
          actionsDisabled={roleWeight <= ROLE_WEIGHTS[row.role] && roleWeight !== 3 && row.id !== userId}
        />
      ),
    },
  ];

  let tableColumns = userColumns;
  tableColumns = [].concat(
    tableColumns,
    entityType === 'organization' ? tokenColumns : []
  );
  tableColumns = [].concat(tableColumns, deleteAccess ? actionColumns : []);

  return (
    <EnergyTracerTable
      data={users}
      columns={tableColumns}
      keyField={'id'}
      deleteUser={deleteUser}
      saveUserRoles={saveUserRoles}
    />
  );
}

UserTable.propTypes = {
  addUserToken: PropTypes.func,
  deleteUserToken: PropTypes.func,
};

UserTable.defaultProps = {
  addUserToken: () => { },
  deleteUserToken: () => { },
};

const UserRoleSelect = (props) => {
  const { rowId, userId, userRole, changeUserRole, disabled, roleWeight } = props;
  return (
    <Form.Group>
      <Form.Control
        data-id='user-role_select'
        as='select'
        value={userRole}
        disabled={disabled || (roleWeight <= ROLE_WEIGHTS[userRole] && roleWeight !== 3 && rowId !== userId)}
        onChange={(e) => {
          changeUserRole(rowId, e.target.value, userRole);
        }}
      >
        {ROLES.map(
          (role) =>
            (roleWeight >= ROLE_WEIGHTS[role] || role === userRole) && (
              <option key={`user-${rowId}-role-${role}`} value={role}>
                {ROLE_NAMES_UPPER[role]}
              </option>
            )
        )}
      </Form.Control>
    </Form.Group>
  );
};

const UserActions = (props) => {
  const { rowId, deleteUser } = props;
  return (
    <Col>
      <button
        className='btn btn-danger'
        onClick={() => {
          deleteUser(rowId);
        }}
      >
        <FontAwesomeIcon
          data-id='delete-user_btn'
          style={{ fontSize: '21px', lineHeight: 'inherit', paddingTop: '2px' }}
          icon={faTrashAlt}
        />
      </button>
    </Col>
  );
};

const UserToken = (props) => {
  const { token, rowId, addUserToken, deleteUserToken, actionsDisabled } =
    props;
  return (
    <>
      {token === null ? (
        <>
          {!actionsDisabled && (
            <Button
              variant='link'
              style={{ width: '100%', margin: '0px', padding: '0px' }}
              onClick={() => addUserToken(rowId)}
            >
              <div style={{ textAlign: 'left', margin: 'auto' }}>
                <FontAwesomeIcon
                  icon={faPlusSquare}
                  style={{ fontSize: '23px' }}
                />
              </div>
            </Button>
          )}
        </>
      ) : (
        <div>
          {token}
          {'  '}
          {!actionsDisabled && (
            <Button
              variant='outline-danger'
              style={{
                padding: '0px',
                borderWidth: '0px',
                borderRadius: '50%',
                height: '28px',
                width: '28px',
              }}
            >
              <FontAwesomeIcon
                style={{ fontSize: '18px', lineHeight: 'inherit' }}
                icon={faTimesCircle}
                onClick={() => deleteUserToken(rowId)}
              />
            </Button>
          )}
        </div>
      )}
    </>
  );
};
